<template>
  <span
    class="cat-chip"
    :class="{
      'is-editable': editable,
      'is-selected': selected,
      'has-action': hasAction
    }"
    :style="chipStyle"
    :title="title"
    @click="$emit('click', $event)"
  >
    <!-- 图标 / 表情 / 无图标时的圆角方形色点。
         图标和表情都**存在分类名称字段里**（'fa:book学习' / '🍕吃饭'），
         由 parseTagName 拆出来。
         **图标色 = 色点色 = 分类原色**（`iconColor`），三者必须一致 ——
         否则同一行里「有图标的分类」和「只有色点的分类」看起来是两种颜色。 -->
    <span v-if="iconType === 'fa'" class="chip-dot chip-dot-icon" :style="{ color: iconColor }">
      <i :class="'fas fa-' + iconValue"></i>
    </span>
    <span v-else-if="iconType === 'emoji'" class="chip-dot chip-dot-emoji" :style="{ color: iconColor }">{{ iconValue }}</span>
    <span
      v-else
      class="chip-dot"
      :style="{ background: iconColor, border: '1px solid rgba(0,0,0,.15)' }"
    ></span>
    <span class="chip-text">{{ text }}</span>
    <!-- 右侧动作区（图例用来放删除 × ），由调用方通过插槽塞进来 -->
    <slot />
  </span>
</template>

<script>
import { parseTagName, categoryPalette, BLOCK_TEXT_COLOR, shadeHexColor } from './constants'

/**
 * 分类标签（胶囊）——**图例与「添加/修改记录」弹窗共用同一份外观**。
 *
 * 用户要求：「添加记录」里的分类和时间表上方「我的分类」里的分类，
 * 外观、样式等**一模一样**。所以这里抽成一个组件，两处只传数据、不再各写一套 CSS，
 * 从根上避免两边再次漂移（历史上图例/弹窗/时间块三处配色已经漂移过好几次）。
 *
 * 配色唯一来源：`constants.categoryPalette(color, name)` + `BLOCK_TEXT_COLOR`。
 * 注意 `label` 必须是**后端存的整串名称**（含 'fa:book' / emoji 前缀），
 * 否则 categoryPalette 命不中默认分类预设、图标也拆不出来。
 */
export default {
  name: 'CategoryChip',
  props: {
    /** 分类颜色（hex，来自后端 tag.color） */
    color: { type: String, default: '' },
    /** 后端存的整串分类名称（含图标前缀），如 'fa:book学习' / '🍕吃饭' */
    label: { type: String, default: '' },
    /** 选中态：整枚点亮成分类原色（底色 = 边框色 = `p.base`）；尺寸/字号/文字色不变 */
    selected: { type: Boolean, default: false },
    /** 可点击：加手型光标 + hover 反馈（图例的标签、弹窗的分类按钮都是可点的） */
    editable: { type: Boolean, default: false },
    /** 右侧有动作按钮（如删除 ×）：预留右侧内边距并允许绝对定位 */
    hasAction: { type: Boolean, default: false },
    title: { type: String, default: '' }
  },
  computed: {
    parsed() {
      return parseTagName(this.label)
    },
    iconType() {
      return this.parsed.iconType
    },
    iconValue() {
      return this.parsed.iconValue
    },
    text() {
      return this.parsed.text
    },
    /** 分类配色（唯一色源 `constants.categoryPalette`），chipStyle 与 iconColor 共用 */
    palette() {
      return categoryPalette(this.color, this.label)
    },
    /**
     * 图标 / 色点的颜色 —— **两者永远同色**。
     *
     * - **未选中：分类原色 `palette.base`**。用户 2026-10-04 要求「分类图标的颜色改成和色点的
     *   颜色一致」：图例里「有图标的分类」显示图标、「无图标的分类」显示色点，此前图标跟文字走
     *   （深灰）、色点是分类原色，同一行里两种颜色并存，看着不统一。
     * - **选中：比原色更高饱和度一档（`shadeHexColor(base, -18)`）**。
     *   用户 2026-10-05 要求「选中时图标不要变黑，改成更高饱和度的颜色」：
     *   选中态底色已是实心原色，若图标保持原色会「隐身」、若用黑灰又太突兀；
     *   把原色整体压暗约 18% 得到一个更浓的同色系，既压在实心底上清晰可见，
     *   又比未选中态更「跳」、明确区分高亮。色点同步跟随，保证「图标 === 色点」恒成立。
     */
    iconColor() {
      return this.selected ? shadeHexColor(this.palette.base, -18) : this.palette.base
    },
    /**
     * 胶囊外观。文字色固定 `BLOCK_TEXT_COLOR`（不随分类色变化）。
     *
     * 选中态（用户要求「选中状态更明显一些，如增加底色的高亮状态」
     * +「底色和边框颜色一致」）：
     * - **底色 = 边框色 = 分类原色 `p.base`**（实心），两者完全一致；
     * - **不改尺寸 / 字号 / 文字色**（仍 `BLOCK_TEXT_COLOR`）——
     *   所以选中态仍是「我的分类」图例里那同一枚胶囊，只是整枚被点亮成分类色。
     *
     * ⚠️ 文字色**不要改成白色**：四个默认分类（蜜桃 `#E99A76` / 天蓝 `#6FA8DC` /
     *    薄荷 `#82B87D` / 暖黄 `#E6B94A`）都是中间调，白字压上去只有 2.2:1 左右、糊成一片；
     *    黑灰 `#2d3748` 反而有 5~7:1。色板 `TAG_COLORS` 也全是浅色系，同理。
     */
    chipStyle() {
      const p = this.palette
      const style = {
        background: p.bg,
        borderColor: p.border,
        color: BLOCK_TEXT_COLOR
      }
      if (this.selected) {
        style.background = p.base
        style.borderColor = p.base
      }
      return style
    }
  }
}
</script>

<style scoped>
/* ===== 分类胶囊：图例「我的分类」与「添加记录」弹窗共用 =====
   下面这些数值就是唯一的「分类标签长什么样」的定义，改这里两处同时变。 */
.cat-chip{
  display:inline-flex;
  align-items:center;
  gap:0.375rem;
  padding:0.25rem 0.625rem;
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  background:var(--lp-surface,#fff);
  color:var(--lp-text-2,#5f7268);
  font-size:0.75rem;
  font-family:inherit;
  user-select:none;
  transition:background 0.15s ease, border-color 0.15s ease;
}
/* 右侧有动作按钮（删除 ×）：留出位置并让按钮能绝对定位 */
.cat-chip.has-action{
  position:relative;
  padding-right:1.375rem;
}
.cat-chip.is-editable{
  cursor:pointer;
}
.cat-chip.is-editable:hover{
  background:var(--lp-surface-2,#f7fbf8);
  border-color:var(--lp-border-strong,#a5cdb3);
}
/* 无图标时的色点：11px **圆角方形**（不是圆点，用户明确要求） */
.chip-dot{
  width:11px;
  height:11px;
  border-radius:3px;
  display:inline-block;
  flex:0 0 auto;
}
/* 图标 / 表情型色块：不设固定背景，靠行高把图标压回 11px 的视觉尺寸 */
.chip-dot-icon,
.chip-dot-emoji{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border:none;
  background:transparent;
  font-size:13px;
  line-height:1;
  width:auto;
  height:auto;
}
.chip-dot-emoji{
  font-size:12px;
}
.chip-text{
  white-space:nowrap;
}
</style>
